import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, GraduationCap, TrendingUp, Printer } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { studentsApi } from '../../students.api';
import type { StudentReportCard } from '../profile.types';
import { TermBlock } from './ExamTables';

// Merged from AcademicsTab + ReportCardsTab:
// - Term filter + print (from ReportCardsTab)
// - Trend chart (from AcademicsTab)
// - Published results only
// - Subject-wise view (single + multi-select) added

const BASE = '/api/parent-app/admin';
interface Term { id: number; name: string }
interface Props { uuid: string }
type ViewMode = 'term' | 'subject';

const AcademicsTab: React.FC<Props> = ({ uuid }) => {
  const [terms, setTerms] = useState<Term[]>([]);
  const [termId, setTermId] = useState<number | ''>('');
  const [card, setCard] = useState<StudentReportCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('term');

  useEffect(() => {
    apiRequest<any>(`${BASE}/exam-terms`).then((r) => setTerms(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    studentsApi.reportCard(uuid, { published: true, term_id: termId || undefined })
      .then((r) => setCard(r.success ? r.data ?? null : null))
      .finally(() => setLoading(false));
  }, [uuid, termId]);
  useEffect(load, [load]);

  const allTerms = card?.terms ?? [];
  const withResults = allTerms.filter((t) => t.exams.length > 0);
  const trend = allTerms
    .filter((t) => t.summary.percent != null)
    .map((t) => ({ name: t.name, pct: t.summary.percent as number }));

  return (
    <div>
      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {viewMode === 'term' && (
          <select
            className="select"
            value={termId}
            onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
            style={{ width: 'auto' }}
          >
            <option value="">All terms</option>
            {terms.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>Published results only</span>
        <div className="seg" style={{ marginLeft: 'auto' }}>
          <button className={viewMode === 'term' ? 'active' : ''} onClick={() => setViewMode('term')}>
            By Term
          </button>
          <button className={viewMode === 'subject' ? 'active' : ''} onClick={() => setViewMode('subject')}>
            By Subject
          </button>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => window.print()} style={{ gap: 6 }}>
          <Printer size={15} /> Print
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--slate)' }} />
        </div>
      ) : !withResults.length ? (
        <div className="empty">
          <div className="empty-art"><GraduationCap size={40} /></div>
          <h3>No published results yet</h3>
          <p>Results will appear here once marks are published for this student.</p>
        </div>
      ) : viewMode === 'term' ? (
        <>
          {trend.length > 1 && !termId && (
            <div className="card card-pad" style={{ marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <TrendingUp size={16} style={{ color: 'var(--text-2)' }} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>Term-wise trend</span>
              </div>
              <TrendBars data={trend} />
            </div>
          )}
          {withResults.map((term) => <TermBlock key={term.id} term={term} />)}
        </>
      ) : (
        <SubjectView terms={withResults} />
      )}
    </div>
  );
};

// ── Subject-wise view ──────────────────────────────────────────────
function SubjectView({ terms }: { terms: StudentReportCard['terms'] }) {
  // Collect all unique subject names in order of first appearance
  const allSubjects: string[] = [];
  for (const term of terms) {
    for (const exam of term.exams) {
      for (const subj of exam.subjects) {
        if (!allSubjects.includes(subj.name)) allSubjects.push(subj.name);
      }
    }
  }

  // None selected by default — admin picks which subjects to view
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allSubjects));
  const allOn = selected.size === allSubjects.length;
  const visibleSubjects = allSubjects.filter((s) => selected.has(s));

  if (!allSubjects.length) return null;

  return (
    <>
      {/* ── Subject pills ── */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '14px 18px',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Select subjects to compare
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {selected.size > 0
              ? <>{selected.size} of {allSubjects.length} selected</>
              : 'None selected'}
            <button
              onClick={allOn ? () => setSelected(new Set()) : selectAll}
              style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: 'var(--slate)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              {allOn ? 'Clear all' : 'Select all'}
            </button>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {allSubjects.map((name) => {
            const on = selected.has(name);
            return (
              <button
                key={name}
                onClick={() => toggle(name)}
                style={{
                  padding: '5px 14px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  border: '1.5px solid',
                  borderColor: on ? 'var(--slate)' : 'var(--border-2)',
                  background: on ? 'var(--slate)' : 'transparent',
                  color: on ? 'var(--accent)' : 'var(--text-3)',
                  cursor: 'pointer',
                  transition: 'all .14s',
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Prompt when nothing selected */}
      {visibleSubjects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--text-3)', fontSize: 13.5, border: '1px dashed var(--border-2)', borderRadius: 14 }}>
          Select one or more subjects above to view marks across all exams.
        </div>
      )}

      {/* ── Matrix table ── */}
      {visibleSubjects.length > 0 && <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={{ ...thBase, textAlign: 'left', minWidth: 120 }}>Term</th>
              <th style={{ ...thBase, textAlign: 'left', minWidth: 140 }}>Exam</th>
              {visibleSubjects.map((s) => (
                <th key={s} style={{ ...thBase, textAlign: 'center', minWidth: 110 }}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {terms.flatMap((term) =>
              term.exams.map((exam, examIdx) => (
                <tr
                  key={`${term.id}-${exam.id}`}
                  style={{ borderBottom: '1px solid var(--divider)' }}
                >
                  {/* Term cell — rowspan groups exams visually */}
                  {examIdx === 0 && (
                    <td
                      rowSpan={term.exams.length}
                      style={{
                        padding: '14px 16px',
                        background: 'var(--surface)',
                        fontWeight: 700,
                        fontSize: 13,
                        color: 'var(--text)',
                        verticalAlign: 'top',
                        borderRight: '1px solid var(--border)',
                        borderBottom: '1px solid var(--border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {term.name}
                    </td>
                  )}

                  {/* Exam name */}
                  <td style={{
                    padding: '12px 16px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    whiteSpace: 'nowrap',
                    borderRight: '1px solid var(--divider)',
                    background: examIdx % 2 === 0 ? 'var(--surface)' : 'var(--surface-tint)',
                  }}>
                    {exam.name}
                  </td>

                  {/* Subject cells */}
                  {visibleSubjects.map((sName) => {
                    const subj = exam.subjects.find((s) => s.name === sName);
                    const bg = examIdx % 2 === 0 ? 'var(--surface)' : 'var(--surface-tint)';

                    if (!subj) {
                      return (
                        <td key={sName} style={{ ...subjectCell, background: bg, color: 'var(--text-3)' }}>
                          —
                        </td>
                      );
                    }

                    if (subj.is_absent) {
                      return (
                        <td key={sName} style={{ ...subjectCell, background: bg }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                            background: 'var(--danger-soft)', color: 'var(--danger)',
                            fontWeight: 700, fontSize: 12,
                          }}>
                            Absent
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={sName} style={{ ...subjectCell, background: bg }}>
                        {/* Marks */}
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                          {subj.marks_obtained != null
                            ? <>{subj.marks_obtained}<span style={{ color: 'var(--text-3)', fontWeight: 500, fontSize: 12 }}>/{subj.max_marks ?? '—'}</span></>
                            : '—'}
                        </div>
                        {/* Percent + grade */}
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexWrap: 'wrap' }}>
                          {subj.percent != null && (
                            <span style={{ fontSize: 12, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
                              {subj.percent}%
                            </span>
                          )}
                          {subj.grade_label && (
                            <span style={{
                              padding: '1px 8px', borderRadius: 999,
                              background: 'var(--accent-soft)', color: 'var(--accent-deep)',
                              fontWeight: 700, fontSize: 11,
                            }}>
                              {subj.grade_label}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
            {/* ── Overall row ── */}
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td style={{
                padding: '14px 16px',
                background: 'var(--slate-soft)',
                fontWeight: 800,
                fontSize: 13,
                color: 'var(--text)',
                whiteSpace: 'nowrap',
                borderRight: '1px solid var(--border)',
              }}>
                Overall
              </td>
              <td style={{
                padding: '12px 16px',
                background: 'var(--slate-soft)',
                color: 'var(--text-3)',
                fontSize: 11,
                fontStyle: 'italic',
                borderRight: '1px solid var(--border)',
                whiteSpace: 'nowrap',
              }}>
                Cumulative total
              </td>
              {visibleSubjects.map((sName) => {
                let totalObtained = 0;
                let totalMax = 0;
                let hasData = false;
                for (const term of terms) {
                  for (const exam of term.exams) {
                    const subj = exam.subjects.find((s) => s.name === sName);
                    if (subj && !subj.is_absent && subj.marks_obtained != null && subj.max_marks != null) {
                      totalObtained += subj.marks_obtained;
                      totalMax += subj.max_marks;
                      hasData = true;
                    }
                  }
                }
                const pct = hasData && totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : null;
                return (
                  <td key={sName} style={{ ...subjectCell, background: 'var(--slate-soft)', borderRight: '1px solid var(--border)' }}>
                    {pct != null ? (
                      <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                        {pct}%
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-3)', fontSize: 13 }}>—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>}
    </>
  );
}

// ── Trend bars ────────────────────────────────────────────────────
function TrendBars({ data }: { data: { name: string; pct: number }[] }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, height: 140, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{d.pct}%</span>
          <div style={{ width: '100%', maxWidth: 54, height: `${(d.pct / 100) * 100}%`, minHeight: 4, background: 'var(--slate)', borderRadius: '6px 6px 0 0' }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-2)', textAlign: 'center' }}>{d.name}</span>
        </div>
      ))}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const thBase: React.CSSProperties = {
  padding: '11px 16px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--text-2)',
  borderBottom: '2px solid var(--border)',
  background: 'var(--slate-soft)',
  whiteSpace: 'nowrap',
};
const subjectCell: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'center',
  borderRight: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

export default AcademicsTab;
